/**
 * Filling one in.
 *
 * The form is drawn from the version this application was started on, never
 * from whatever the product looks like now - which is what lets a product be
 * changed at lunchtime without breaking anything filled in that morning.
 *
 * Saving is a debounced background write. A long application is worked on over
 * days, so a draft is saved with whatever it has and the problems are reported
 * rather than enforced; submitting is where they become blocking.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { headline, type AnswerDocument, type ValidationIssue } from "@dp/shared";
import { ApiError, api } from "../../lib/api.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import { BackLink, Loading, PageHead, Problem } from "../../components/Page.tsx";
import { Form } from "../../renderer/index.ts";
import type { ApplicationDetail } from "./types.ts";

const SAVE_DELAY = 700;

export function ApplicationEditor({ id }: { id: string }) {
  const { can } = useAuth();
  const detail = useAsync(() => api.get<ApplicationDetail>(`/api/applications/${id}`), [id]);

  if (detail.loading && !detail.data) return <Loading what="the application" />;
  if (detail.error) return <Problem error={detail.error} />;
  if (!detail.data) return null;

  return <Editor key={id} detail={detail.data} canEdit={can("applications", "edit")} onReload={detail.reload} />;
}

type SaveState = "clean" | "saving" | "saved" | "failed";

function Editor({ detail, canEdit, onReload }: { detail: ApplicationDetail; canEdit: boolean; onReload: () => void }) {
  const [answers, setAnswers] = useState<AnswerDocument>(detail.application.answers ?? {});
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [saveState, setSaveState] = useState<SaveState>("clean");
  const [problem, setProblem] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitted = detail.application.status !== "draft";
  const readOnly = submitted || !canEdit;

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(answers);
  latest.current = answers;

  const save = useCallback(async () => {
    setSaveState("saving");
    try {
      const result = await api.put<{ answers: AnswerDocument; issues: ValidationIssue[] }>(
        `/api/applications/${detail.application.id}/answers`,
        { answers: latest.current },
      );
      setIssues(result.issues);
      setSaveState("saved");
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not save.");
      setSaveState("failed");
    }
  }, [detail.application.id]);

  function change(next: AnswerDocument) {
    setAnswers(next);
    setSaveState("saving");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void save(), SAVE_DELAY);
  }

  // A pending save must not be lost because the user navigated away.
  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void save();
      }
    };
  }, [save]);

  async function submit() {
    setProblem(null);
    setSubmitting(true);
    if (timer.current) clearTimeout(timer.current);
    try {
      await save();
      await api.post(`/api/applications/${detail.application.id}/submit`);
      onReload();
    } catch (error) {
      if (error instanceof ApiError) {
        setProblem(error.message);
        if (error.details.length) setIssues(error.details);
      } else {
        setProblem("Could not submit this application.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="stack">
      <BackLink to="/applications">Applications</BackLink>

      <PageHead
        title={detail.application.reference}
        sub={<>
          {detail.productName} · v{detail.compiled.version}
          {headline(detail.compiled, answers) ? ` · ${headline(detail.compiled, answers)}` : ""}
        </>}
        actions={
          <div className="row">
            {!readOnly && <SaveBadge state={saveState} />}
            {submitted
              ? <span className="chip chip-ok">Submitted</span>
              : canEdit && (
                <button className="btn btn-primary" type="button" onClick={() => void submit()} disabled={submitting}>
                  {submitting ? "Submitting…" : "Submit"}
                </button>
              )}
          </div>
        }
      />

      {problem && <div className="notice notice-bad">{problem}</div>}

      {detail.versionSuperseded && !submitted && (
        <div className="notice notice-info">
          A newer version of {detail.productName} has been published since this application was started. This one keeps
          asking the questions it began with — that is deliberate, and it will submit against v{detail.compiled.version}.
        </div>
      )}

      {submitted && (
        <div className="notice notice-ok">
          Submitted {detail.application.submittedAt ? new Date(detail.application.submittedAt).toLocaleString() : ""}.
          The answers are fixed.
        </div>
      )}

      {issues.length > 0 && !submitted && (
        <div className="notice notice-warn">
          <strong>{issues.length} {issues.length === 1 ? "answer needs" : "answers need"} attention</strong>
          <ul className="issue-list">
            {issues.slice(0, 8).map((issue) => <li key={issue.path}>{issue.message}</li>)}
            {issues.length > 8 && <li>…and {issues.length - 8} more.</li>}
          </ul>
        </div>
      )}

      <Form
        product={detail.compiled}
        answers={answers}
        readOnly={readOnly}
        issues={issues}
        onChange={change}
      />
    </div>
  );
}

function SaveBadge({ state }: { state: SaveState }) {
  if (state === "clean") return null;
  const text = state === "saving" ? "Saving…" : state === "saved" ? "Saved" : "Not saved";
  const tone = state === "failed" ? "chip-bad" : state === "saved" ? "chip-ok" : "";
  return <span className={`chip ${tone}`}>{text}</span>;
}
